import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '@ui-nuvertos/shared';

// Search result interface
export interface SearchResult {
  entity_type: string;
  entity_id: string;
  title: string;
  subtitle: string;
  identifier: string;
  status: string;
  amount?: number;
  date?: string;
  relevance_score: number;
  route?: string; // Now optional, route is computed on frontend
  entity_label: string;
}

// Feature search result for navigation items
export interface FeatureResult {
  label: string;
  route: string;
  icon: string;
  description?: string;
  category?: string;
  keywords?: string[];
}

// Search response interface
export interface SearchResponse {
  results: SearchResult[];
  total_count: number;
  query: string;
  module?: string;
}

// Module option for search filtering
export interface ModuleOption {
  label: string;
  prefix: string;
  icon: string;
  color: string;
}

@Injectable({
  providedIn: 'root'
})
export class GlobalSearchService {
  private API_BASE_URL = environment.accountingUrl;

  setApiBaseUrl(url: string): void {
    this.API_BASE_URL = url;
  }

  private allowedEntityTypes: string[] = [];

  setAllowedEntityTypes(types: string[]): void {
    this.allowedEntityTypes = types;
  }

  // Module options for autocomplete suggestions — set by each app via layout config
  moduleOptions: ModuleOption[] = [];

  setModuleOptions(options: ModuleOption[]): void {
    this.moduleOptions = options;
  }

  // Entity type to icon mapping
  private readonly entityIcons: Record<string, string> = {
    vendor: 'pi-truck',
    vendor_invoice: 'pi-file-edit',
    vendor_po: 'pi-shopping-cart',
    vendor_grn: 'pi-inbox',
    vendor_return: 'pi-replay',
    vendor_debit_note: 'pi-file-excel',
    vendor_payment: 'pi-wallet',
    client: 'pi-user',
    client_invoice: 'pi-file',
    client_order: 'pi-shopping-bag',
    client_return: 'pi-undo',
    client_credit_note: 'pi-file-export',
    client_payment: 'pi-credit-card',
    client_refund: 'pi-money-bill',
    item: 'pi-box',
    journal_voucher: 'pi-book',
    inventory_adjustment: 'pi-sliders-h',
    feature: 'pi-th-large'
  };

  // Entity type to color mapping
  private readonly entityColors: Record<string, string> = {
    vendor: '#7C3AED',
    vendor_invoice: '#8B5CF6',
    vendor_po: '#F59E0B',
    vendor_grn: '#6366F1',
    vendor_return: '#EF4444',
    vendor_debit_note: '#DC2626',
    vendor_payment: '#14B8A6',
    client: '#0EA5E9',
    client_invoice: '#10B981',
    client_order: '#3B82F6',
    client_return: '#EC4899',
    client_credit_note: '#F472B6',
    client_payment: '#22D3EE',
    client_refund: '#F97316',
    item: '#14B8A6',
    journal_voucher: '#6366F1',
    inventory_adjustment: '#8B5CF6',
    feature: '#3B82F6'
  };

  constructor(private http: HttpClient) {}

  /**
   * Search across all entities
   * @param query Search query (can include module prefix like "Vendor: ABC")
   * @param limit Number of results to return (default: 20)
   * @param offset Pagination offset (default: 0)
   * @param entityTypes Optional array of entity types to filter
   */
  search(query: string, limit = 20, offset = 0, entityTypes?: string[]): Observable<SearchResponse> {
    if (!query || query.trim().length < 2) {
      return of({ results: [], total_count: 0, query: '' });
    }

    let params = new HttpParams()
      .set('q', query.trim())
      .set('limit', limit.toString())
      .set('offset', offset.toString());

    const typesToFilter = entityTypes?.length ? entityTypes : this.allowedEntityTypes;
    if (typesToFilter?.length) {
      params = params.set('types', typesToFilter.join(','));
    }

    return this.http.get<SearchResponse>(`${this.API_BASE_URL}/api/v1/global-search`, {
      params,
      withCredentials: true
    });
  }

  /**
   * Get recent items for the search dropdown
   * @param limit Number of results to return (default: 10)
   */
  getRecentItems(limit = 10): Observable<{ results: SearchResult[] }> {
    const params = new HttpParams().set('limit', limit.toString());

    return this.http.get<{ results: SearchResult[] }>(`${this.API_BASE_URL}/api/v1/global-search/recent`, {
      params,
      withCredentials: true
    });
  }

  /**
   * Get available module options for the search bar
   */
  getModuleOptions(): Observable<{ modules: ModuleOption[] }> {
    return this.http.get<{ modules: ModuleOption[] }>(`${this.API_BASE_URL}/api/v1/global-search/modules`, {
      withCredentials: true
    });
  }

  /**
   * Get entity icon based on type
   */
  getEntityIcon(entityType: string): string {
    return this.entityIcons[entityType] || 'pi-search';
  }

  /**
   * Get entity color for visual distinction
   */
  getEntityColor(entityType: string): string {
    return this.entityColors[entityType] || '#6B7280';
  }

  /**
   * Format entity type for display
   */
  formatEntityType(entityType: string): string {
    return entityType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Get status badge class
   */
  getStatusClass(status: string): string {
    const statusClasses: Record<string, string> = {
      approved: 'status-approved',
      pending: 'status-pending',
      rejected: 'status-rejected',
      open: 'status-open',
      closed: 'status-closed',
      draft: 'status-draft',
      not_required: 'status-neutral',
      unsettled: 'status-pending',
      partially_paid: 'status-warning',
      settled: 'status-approved'
    };
    return statusClasses[status?.toLowerCase()] || 'status-neutral';
  }

  /**
   * Format status for display
   */
  formatStatus(status: string): string {
    if (!status) return '';
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Format amount with currency
   */
  formatAmount(amount: number | undefined): string {
    if (amount === undefined || amount === null) return '';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  }

  /**
   * Check if a module prefix is present in the query
   */
  hasModulePrefix(query: string): boolean {
    const modulePrefixes = this.moduleOptions.map(m => m.prefix.toLowerCase());
    const queryLower = query.toLowerCase().trim();
    return modulePrefixes.some(prefix => queryLower.startsWith(prefix.toLowerCase()));
  }

  /**
   * Extract module name from query
   */
  extractModuleFromQuery(query: string): string | null {
    const match = query.match(/^(\w+)\s*:/);
    return match ? match[1].toLowerCase() : null;
  }

  /**
   * Extract search term from query (after module prefix)
   */
  extractSearchTermFromQuery(query: string): string {
    const match = query.match(/^(\w+)\s*:\s*(.*)$/);
    return match ? match[2].trim() : query.trim();
  }

  /**
   * Registry of searchable features (populated from sidebar config)
   */
  private featureRegistry: FeatureResult[] = [];

  /**
   * Register features for local search (e.g., from sidebar config)
   * @param features Array of feature items to register
   */
  registerFeatures(features: FeatureResult[]): void {
    this.featureRegistry = features;
  }

  /**
   * Get all registered features
   */
  getRegisteredFeatures(): FeatureResult[] {
    return this.featureRegistry;
  }

  /**
   * Search features locally (no API call)
   * Searches both static featureShortcuts and dynamically registered features
   * @param query Search query
   * @param limit Maximum number of results
   * @returns Array of matching features
   */
  searchFeatures(query: string, limit = 5): FeatureResult[] {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const queryLower = query.toLowerCase().trim();
    
    // Combine static shortcuts with registered features
    const allFeatures = [...this.featureRegistry];
    
    // Score each feature based on match quality
    const scoredFeatures = this.featureRegistry
      .map(feature => {
        let score = 0;
        const labelLower = feature.label.toLowerCase();
        const categoryLower = feature.category?.toLowerCase() || '';
        
        // Exact label match
        if (labelLower === queryLower) {
          score = 100;
        }
        // Label starts with query
        else if (labelLower.startsWith(queryLower)) {
          score = 80;
        }
        // Label contains query
        else if (labelLower.includes(queryLower)) {
          score = 60;
        }
        // Category matches
        else if (categoryLower && categoryLower.includes(queryLower)) {
          score = 40;
        }
        // Keywords match
        else if (feature.keywords?.some(kw => kw.toLowerCase().includes(queryLower))) {
          score = 30;
        }
        
        return { feature, score };
      })
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map(item => item.feature);

    return scoredFeatures;
  }
}

